import { TreeNode } from "primeng/api";

export const isEmptyObject = (obj: unknown): boolean => {
    return obj !== null && typeof obj === 'object' && !Array.isArray(obj) && Object.keys(obj).length === 0;
}

/**
 * Kiểm tra một giá trị có được coi là "rỗng" hay không (dùng cho filter query params).
 * - null, undefined, "", 0 -> rỗng.
 * - Object: rỗng nếu không có key hoặc mọi value bên trong đều rỗng (đệ quy).
 *   VD: { label: "" } -> rỗng (tránh đưa lên URL dạng leadSources={"label":""}).
 * - Array: rỗng nếu length = 0 hoặc mọi phần tử đều rỗng.
 */
export const isEmptyValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (typeof value === 'number') return value === 0;
    if (typeof value === 'boolean') return false;
    // Date
    if (value instanceof Date) return false;

    if (Array.isArray(value)) {
        return value.length === 0 || value.every(item => isEmptyValue(item));
    }

    if (typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length === 0) return true;
        return keys.every(k => isEmptyValue((value as Record<string, unknown>)[k]));
    }

    return false;
};

export const pickNotEmpty = <T extends Record<string, any>>(obj: T): Partial<T> => {
    return Object.fromEntries(
        Object.entries(obj).filter(([_, value]) => {
            if (Array.isArray(value)) {
                // Lọc bỏ từng phần tử rỗng (kể cả object chỉ có label: "").
                const filtered = value.filter(item => !isEmptyValue(item));
                return filtered.length > 0;
            }
            return !isEmptyValue(value);
        })
    ) as Partial<T>;
}

export const serializeParams = (params: Record<string, any>) => {
  /**
   * NOTE:
   * - Hàm này dùng để convert object params sang dạng có thể đưa lên URL (queryParams).
   * - Nếu để nguyên object / array of object thì Angular sẽ convert thành "[object Object]"
   *   và khi encode sẽ thành "%5Bobject%20Object%5D" (hiện tượng bạn đang gặp là "%object%").
   * - Ở đây ta chuẩn hoá lại:
   *   + Array primitive (string / number / boolean): join bằng dấu phẩy.
   *   + Array object: cố gắng lấy trường "id" | "key" | "value" để join.
   *   + Object đơn lẻ: stringify bằng JSON.stringify để tránh "[object Object]".
   *
   * Mục tiêu: đơn giản, dễ hiểu, hạn chế thay đổi behaviour các chỗ khác.
   */

  const extractFromObject = (item: any): string | number | boolean | null => {
    if (!item || typeof item !== 'object') return null;

    // Các pattern phổ biến trong project:
    // - UserDetail: có field "id"
    // - TreeNode: thường có "key"
    // - Một số object khác dùng "value"
    if ('id' in item && item.id != null) return item.id as string | number;
    if ('key' in item && item.key != null) return item.key as string | number;
    if ('value' in item && item.value != null) return item.value as string | number;

    // Fallback: không đoán được, để null -> sẽ được stringify ở nhánh object bên dưới.
    return null;
  };

  return Object.entries(params).reduce((acc, [key, value]) => {
    // Bỏ qua hoàn toàn undefined / null (router sẽ không gán query param).
    if (value === undefined || value === null) {
      return acc;
    }

    // Primitive: string / number / boolean -> gán trực tiếp (kiểm tra trước array để tránh string bị xử lý như array).
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      if (value === '') return acc; // Bỏ qua chuỗi rỗng.
      acc[key] = value;
      return acc;
    }

    // Trường hợp là array.
    if (Array.isArray(value)) {
      const mapped = value
        .map(item => {
          if (item === undefined || item === null) return null;

          // Primitive -> giữ nguyên.
          if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
            return item;
          }

          // Object -> cố gắng extract id/key/value.
          const extracted = extractFromObject(item);
          if (extracted !== null) return extracted;

          // Fallback: stringify object để tránh "[object Object]".
          try {
            return JSON.stringify(item);
          } catch {
            // Nếu stringify lỗi thì bỏ qua phần tử này.
            return null;
          }
        })
        // Loại bỏ phần tử null sau khi map.
        .filter(item => item !== null) as Array<string | number | boolean>;

      // Nếu sau khi xử lý array rỗng thì không add key vào acc (tránh query param rác).
      if (!mapped.length) return acc;

      acc[key] = mapped.join(',');
      return acc;
    }

    // Trường hợp là object (không phải array).
    if (typeof value === 'object') {
      try {
        acc[key] = JSON.stringify(value);
      } catch {
        // Fallback cuối cùng: cast sang string để không bị "[object Object]".
        acc[key] = String(value);
      }
      return acc;
    }

    // Fallback cuối cùng (không nên đến đây nếu đã xử lý string ở trên).
    acc[key] = String(value);
    return acc;
  }, {} as Record<string, any>);
};

export const findTreeNodesByKeys = (
      treeNodes: TreeNode[],
      keys: string[],
      result: TreeNode[] = [],
      visited = new Set<string>()
    ): TreeNode[] => {
      for (const node of treeNodes) {
        const key = node.key as string;
  
        // match key + chưa từng add
        if (keys.includes(key) && !visited.has(key)) {
          result.push(node);
          visited.add(key);
        }
  
        // Duyệt tiếp children
        if (node.children?.length) {
          findTreeNodesByKeys(node.children, keys, result, visited);
        }
      }
  
      return result;
    }

  /**
   * Helper: parse query param thành string[].
   * - Hỗ trợ cả dạng "a,b,c" lẫn array.
   * - Tự động trim và bỏ phần tử rỗng.
   */
  export const parseArrayParam = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map(item => (item != null ? String(item) : ''))
        .map(item => item.trim())
        .filter(item => !!item);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(item => !!item);
    }
    return [String(value).trim()].filter(item => !!item);
  };

  /**
   * Delimiter dùng để ghép nhiều tên user trên URL (tránh dùng comma vì tên có thể chứa dấu phẩy).
   * Dùng khi serialize userNames / commissionOwnerNames / commissionAgentNames.
   */
  export const DISPLAY_NAMES_URL_DELIMITER = '||';

  /**
   * Lấy fullName từ object user (VD: UserDetail), trim khoảng trắng thừa.
   * Dùng khi ghi tên lên URL (report product-details, lead, performance, note).
   */
  export const extractFullName = (user: any): string => {
    if (!user || typeof user !== 'object') return '';
    const name = user.fullName;
    if (!name || typeof name !== 'string') return '';
    return name.trim();
  };

  /**
   * Validate và clean fullName từ object có fullName property.
   * Trả về chuỗi rỗng nếu dữ liệu không hợp lệ (VD: từng ký tự bị tách bởi spaces).
   * @param obj - object có property fullName (VD: UserDetail)
   * @returns fullName đã trim và replace delimiter, hoặc '' nếu không hợp lệ
   */
  export const getValidFullName = (obj: { fullName?: string } | null | undefined): string => {
    const name = obj?.fullName;
    if (!name || typeof name !== 'string') return '';
    const trimmed = name.trim();
    // Kiểm tra dữ liệu lỗi: nếu không có chuỗi >= 2 chữ cái liền nhau → có thể là dữ liệu corrupt.
    if (trimmed.length > 0 && !/[a-zA-ZÀ-ỹ]{2,}/u.test(trimmed)) {
      return '';
    }
    return trimmed.replace(new RegExp(DISPLAY_NAMES_URL_DELIMITER, 'g'), ' ');
  };

  /**
   * Parse query param chứa chuỗi tên đã join bằng DISPLAY_NAMES_URL_DELIMITER.
   * Trả về mảng tên theo thứ tự (để map 1-1 với mảng ids).
   */
  export const parseDisplayNamesParam = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      // Router có thể trả về ["Name1||Name2"] (mảng 1 phần tử) → tách bằng ||.
      return value
        .flatMap(item => (item != null ? String(item) : '').split(DISPLAY_NAMES_URL_DELIMITER))
        .map(s => s.trim());
    }
    if (typeof value === 'string') {
      return value
        .split(DISPLAY_NAMES_URL_DELIMITER)
        .map(item => item.trim());
    }
    return [String(value).trim()];
  };